if (!customElements.get('newsletter-popup')) {
  const STORAGE_SUBSCRIBED = 'newsletter_subscribed';
  const STORAGE_DISMISSED = 'newsletter_dismissed_at';
  const DAY_MS = 86400000;

  class NewsletterPopup extends HTMLElement {
    constructor() {
      super();
      const parsedDelay = parseInt(this.dataset.delay, 10);
      this.delay = Number.isNaN(parsedDelay) ? 5 : parsedDelay;
      this.dismissDays = parseInt(this.dataset.dismissDays, 10) || 30;
      this.onSubmit = this.onSubmit.bind(this);
      this.onModalClose = this.onModalClose.bind(this);
      this.onDismiss = this.onDismiss.bind(this);
      this.isSubmitting = false;
      this.hasSubmittedSuccessfully = false;
    }

    connectedCallback() {
      if (Shopify?.designMode || this.initialized) return;
      this.initialized = true;

      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;

      this.notifications = this.form.querySelector('[data-notifications]');
      this.submitButton = this.form.querySelector('button.submit');

      if (window.Shopify?.captcha) {
        window.Shopify.captcha.protect(this.form);
      }

      if (this.isReturningSuccess()) {
        this.handleSuccess();
        return;
      }

      if (!this.shouldShow()) return;

      this.form.addEventListener('submit', this.onSubmit);
      this.querySelector('.newsletter-popup__dismiss')?.addEventListener('click', this.onDismiss);
      document.getElementById(`ModalClose-Newsletter-Popup-${this.dataset.sectionId}`)?.addEventListener('click', this.onModalClose);

      this.scheduleShow();
    }

    disconnectedCallback() {
      clearTimeout(this.showTimeout);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    scheduleShow() {
      this.waitForModalReady().then(() => {
        if (this.delay > 0) {
          clearTimeout(this.showTimeout);
          this.showTimeout = setTimeout(() => this.openPopup(), this.delay * 1000);
        } else {
          this.openPopup();
        }
      });
    }

    waitForModalReady() {
      const modal = this.getModal();
      if (!modal) return Promise.resolve();

      return customElements.whenDefined('modal-dialog');
    }

    openPopup() {
      if (this.hasOpened) return;

      const modal = this.getModal();
      if (!modal?.show) return;

      this.hasOpened = true;
      modal.removeAttribute('hidden');
      modal.show();
    }

    getModal() {
      return document.getElementById(this.dataset.modalId);
    }

    getCaptchaToken() {
      return this.form.querySelector('[name="h-captcha-response"]')?.value ||
        this.form.querySelector('[name="g-recaptcha-response"]')?.value;
    }

    isReturningSuccess() {
      return this.checkUrlForSuccess() ||
        !!this.form.querySelector('.form-notification.success');
    }

    checkUrlForSuccess() {
      const url = new URL(window.location.href);
      const { search, hash } = url;

      return url.searchParams.has('contact') ||
        url.searchParams.has('customer_posted') ||
        url.searchParams.has('contact_posted') ||
        search.includes('customer_posted=true') ||
        search.includes('contact_posted=true') ||
        hash.includes('customer_posted') ||
        hash.includes('contact_posted') ||
        hash.includes('NewsletterPopup') ||
        url.pathname.includes('/contact') ||
        search.includes('form_type=customer');
    }

    shouldShow() {
      try {
        if (localStorage.getItem(STORAGE_SUBSCRIBED) || sessionStorage.getItem(STORAGE_SUBSCRIBED)) {
          return false;
        }

        const dismissedAt = localStorage.getItem(STORAGE_DISMISSED);
        if (!dismissedAt) return true;

        const elapsed = Date.now() - parseInt(dismissedAt, 10);
        return Number.isNaN(elapsed) || elapsed >= this.dismissDays * DAY_MS;
      } catch (e) {
        return true;
      }
    }

    onSubmit(event) {
      if (this.isSubmitting) {
        event.preventDefault();
        return;
      }

      if (!this.form.checkValidity()) {
        event.preventDefault();
        this.form.reportValidity();
        return;
      }

      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.notifications.innerHTML = '';

      if (this.getCaptchaToken()) {
        event.preventDefault();
        this.submitViaFetch();
        return;
      }

      clearTimeout(this.submitTimeout);
      this.submitTimeout = setTimeout(() => {
        if (!this.isSubmitting) return;
        this.isSubmitting = false;
        this.submitButton.disabled = false;
      }, 120000);

      // No captcha token yet — let Shopify's hCaptcha handler run and
      // submit the form natively (return_to brings the user back).
    }

    async submitViaFetch() {
      try {
        const body = new URLSearchParams(new FormData(this.form)).toString();
        const response = await fetch(this.form.action, {
          method: 'POST',
          body,
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const html = await response.text();

        if (this.isSuccess(html)) {
          this.handleSuccess();
        } else {
          this.showError(html);
        }
      } catch (error) {
        this.showError();
      } finally {
        this.isSubmitting = false;
        this.submitButton.disabled = false;
      }
    }

    markSubscribed() {
      try {
        localStorage.setItem(STORAGE_SUBSCRIBED, 'true');
        localStorage.removeItem(STORAGE_DISMISSED);
      } catch (e) {
        sessionStorage.setItem(STORAGE_SUBSCRIBED, 'true');
      }
    }

    handleSuccess() {
      clearTimeout(this.showTimeout);
      this.hasOpened = true;
      this.hasSubmittedSuccessfully = true;
      this.markSubscribed();

      if (this.isReturningSuccess()) {
        this.cleanSuccessUrl();
      }

      this.closePopup();
    }

    cleanSuccessUrl() {
      const url = new URL(window.location.href);

      ['contact', 'customer_posted', 'contact_posted'].forEach((param) => {
        url.searchParams.delete(param);
      });

      if (url.hash.includes('NewsletterPopup')) {
        url.hash = '';
      }

      const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    isSuccess(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const form = doc.getElementById(this.form.id);

      if (form?.querySelector('.form-notification.success')) return true;
      if (doc.querySelector('.form-notification.success')) return true;

      const formPosted = doc.querySelector('form[action*="contact"]');
      if (formPosted?.querySelector('.form-notification.success')) return true;

      return html.includes(this.dataset.successMessage) ||
        html.includes('customer_posted=true') ||
        html.includes('contact_posted=true');
    }

    showError(html = '') {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const form = doc.getElementById(this.form.id);
      const error = form?.querySelector('.form-notification.error')?.textContent?.trim();

      const node = document.createElement('div');
      node.className = 'form-notification error';
      node.setAttribute('role', 'alert');
      node.textContent = error || this.dataset.errorMessage;
      this.notifications.appendChild(node);
    }

    closePopup() {
      const modal = this.getModal();

      if (modal?.hide) {
        modal.hide();
      } else {
        modal?.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }

      modal?.setAttribute('hidden', '');
    }

    onModalClose() {
      if (!this.hasSubmittedSuccessfully) {
        this.rememberDismiss();
      }
    }

    onDismiss() {
      this.closePopup();
      if (!this.hasSubmittedSuccessfully) {
        this.rememberDismiss();
      }
    }

    rememberDismiss() {
      try {
        if (!localStorage.getItem(STORAGE_SUBSCRIBED) && !sessionStorage.getItem(STORAGE_SUBSCRIBED)) {
          localStorage.setItem(STORAGE_DISMISSED, String(Date.now()));
        }
      } catch (e) {
        console.warn('Newsletter popup: localStorage unavailable');
      }
    }
  }

  customElements.define('newsletter-popup', NewsletterPopup);
}
